import { getIntegrationIcon, getIntegrationInfo } from './settings/integrationCatalog';
import { settingsTranslator } from '../translations/settings';
import { useLanguage } from '../contexts/LanguageContext';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../utils/backendURL';
import { settingsNotifications } from './settings/settingsNotifications';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { CheckCircle, XCircle, Trash2, Settings, ExternalLink, Loader2 } from 'lucide-react';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const Integrations = () => {
  const { language } = useLanguage();
  const t = settingsTranslator(language);
  const { showSuccess, showError } = settingsNotifications(language);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ show: false, id: null, name: '' });
  const [formData, setFormData] = useState({
    integration_type: '',
    webhook_url: '',
    channel_name: '',
    // Jira fields
    jira_site_url: '',
    jira_email: '',
    jira_api_token: '',
    jira_project_key: '',
    jira_issue_type: 'Bug',
    auto_create_on_failure: false
  });
  const [testingConnection, setTestingConnection] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchIntegrations();
    }
  }, []);

  const fetchIntegrations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/integrations`, {
        headers: getAuthHeaders()
      });
      setIntegrations(response.data);
    } catch (error) {
      console.error('Failed to fetch integrations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenSetup = (type, existing = null) => {
    setSelectedIntegration(existing);
    
    if (type === 'jira') {
      setFormData({
        integration_type: type,
        webhook_url: '',
        channel_name: '',
        jira_site_url: existing?.jira_site_url || '',
        jira_email: existing?.jira_email || '',
        jira_api_token: '', // Always empty for security - will show placeholder
        jira_project_key: existing?.jira_project_key || '',
        jira_issue_type: existing?.jira_issue_type || 'Bug',
        auto_create_on_failure: existing?.auto_create_on_failure || false
      });
    } else {
      setFormData({
        integration_type: existing ? existing.integration_type : type,
        webhook_url: '', // Always empty for security - will show placeholder
        channel_name: existing ? existing.channel_name : '',
        jira_site_url: '',
        jira_email: '',
        jira_api_token: '',
        jira_project_key: '',
        jira_issue_type: 'Bug',
        auto_create_on_failure: false
      });
    }
    
    setError('');
    setSuccess('');
    setShowSetupModal(true);
  };

  const handleTestConnection = async () => {
    if (!selectedIntegration?.id) {
      setError(t("Please save the integration first before testing"));
      return;
    }

    try {
      setTestingConnection(true);
      setError('');
      const response = await axios.post(`${BACKEND_URL}/api/integrations/${selectedIntegration.id}/test`, {}, {
        headers: getAuthHeaders()
      });
      
      if (formData.integration_type === 'jira' && response.data.issue_url) {
        setSuccess(
          <>
            {t(response.data.message) || t("Test issue created successfully!")}
            {' '}
            <a 
              href={response.data.issue_url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline inline-flex items-center"
            >
              {t("View issue")} {response.data.issue_key} <ExternalLink className="h-3 w-3 ml-1" />
            </a>
          </>
        );
      } else {
        setSuccess(t(response.data.message) || t("Test notification sent successfully!"));
      }
    } catch (error) {
      console.error('Failed to test connection:', error);
      let errorMessage = t("Failed to send test notification");
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Handle both string and object/array error details
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map(err => {
            const field = err.loc ? err.loc.join('.') : 'field';
            return `${field}: ${err.msg}`;
          }).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      }
      
      setError(errorMessage);
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      
      if (selectedIntegration?.id) {
        // Update existing
        const updateData = {};
        
        if (formData.integration_type === 'jira') {
          if (formData.jira_site_url) updateData.jira_site_url = formData.jira_site_url;
          if (formData.jira_email) updateData.jira_email = formData.jira_email;
          if (formData.jira_api_token) updateData.jira_api_token = formData.jira_api_token;
          if (formData.jira_project_key) updateData.jira_project_key = formData.jira_project_key;
          if (formData.jira_issue_type) updateData.jira_issue_type = formData.jira_issue_type;
          updateData.auto_create_on_failure = formData.auto_create_on_failure;
        } else {
          if (formData.webhook_url) updateData.webhook_url = formData.webhook_url;
          if (formData.channel_name) updateData.channel_name = formData.channel_name;
        }
        
        await axios.put(`${BACKEND_URL}/api/integrations/${selectedIntegration.id}`, updateData, {
          headers: getAuthHeaders()
        });
      } else {
        // Create new
        const response = await axios.post(`${BACKEND_URL}/api/integrations`, formData, {
          headers: getAuthHeaders()
        });
        setSelectedIntegration(response.data);
      }
      
      await fetchIntegrations();
      setSuccess(t("Integration saved successfully!"));
      
      // Don't close modal yet, allow testing
    } catch (error) {
      console.error('Failed to save integration:', error);
      let errorMessage = t("Failed to save integration");
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Handle both string and object/array error details (Pydantic validation errors)
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Format validation errors from Pydantic
          errorMessage = detail.map(err => {
            const field = err.loc ? err.loc.join('.') : 'field';
            return `${field}: ${err.msg}`;
          }).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = JSON.stringify(detail);
        }
      }
      
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id, integrationName) => {
    setDeleteConfirmation({ show: true, id, name: integrationName });
  };

  const confirmDelete = async () => {
    const { id } = deleteConfirmation;
    setDeleteConfirmation({ show: false, id: null, name: '' });

    try {
      await axios.delete(`${BACKEND_URL}/api/integrations/${id}`, {
        headers: getAuthHeaders()
      });
      await fetchIntegrations();
      showSuccess(t("Integration deleted successfully"));
    } catch (error) {
      console.error('Failed to delete integration:', error);
      showError(t("Failed to delete integration"));
    }
  };

  const handleToggleActive = async (integration) => {
    try {
      await axios.put(`${BACKEND_URL}/api/integrations/${integration.id}`, {
        is_active: !integration.is_active
      }, {
        headers: getAuthHeaders()
      });
      await fetchIntegrations();
    } catch (error) {
      console.error('Failed to update integration:', error);
    }
  };

  const getUserIntegrations = (type) => {
    return integrations.filter(i => i.integration_type === type);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const integrationTypes = [
    { type: 'slack', title: 'Slack', description: t("Get real-time notifications in Slack when tests complete") },
    { type: 'discord', title: 'Discord', description: t("Receive test results and alerts in your Discord server") },
    { type: 'jira', title: 'Jira', description: t("One-click bug reports with screenshots, logs, and repro steps") }
  ];

  return (
    <div className="space-y-6">
      {/* Integration List */}
      <div className="space-y-1">
        {integrationTypes.map((integrationType, idx) => {
          const userIntegrations = getUserIntegrations(integrationType.type);
          const hasIntegrations = userIntegrations.length > 0;

          return (
            <div key={integrationType.type}>
              {/* Integration Header */}
              <div className="py-6">
                <div className="flex items-start space-x-4">
                  {/* Icon */}
                  <div className="flex-shrink-0 mt-1">
                    {getIntegrationIcon(integrationType.type)}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    {/* Title and Description */}
                    <div className="mb-3">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {integrationType.title}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {t(integrationType.description)}
                      </p>
                    </div>

                    {/* Add Channel/Project Button */}
                    <div className="mb-4">
                      <Button
                        onClick={() => handleOpenSetup(integrationType.type)}
                        size="sm"
                        variant="outline"
                        className="text-purple-600 border-purple-300 hover:bg-purple-50"
                      >
                        {hasIntegrations 
                          ? (integrationType.type === 'jira' ? t("+ Add Project") : t("+ Add Channel"))
                          : t("Connect")}
                      </Button>
                    </div>

                    {/* List of Configured Integrations */}
                    {hasIntegrations && (
                      <div className="space-y-2">
                        {userIntegrations.map((integration) => (
                          <div
                            key={integration.id}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                          >
                            {/* Left: Status and Info */}
                            <div className="flex items-center space-x-3">
                              <Badge 
                                variant={integration.is_active ? "default" : "secondary"}
                                className={integration.is_active ? "bg-green-500" : ""}
                              >
                                {integration.is_active ? t("Active") : t("Paused")}
                              </Badge>
                              
                              {integrationType.type === 'jira' ? (
                                <div className="flex items-center space-x-2">
                                  <span className="text-sm font-medium text-gray-700">
                                    {integration.jira_project_key}
                                  </span>
                                  {integration.auto_create_on_failure && (
                                    <Badge variant="outline" className="text-xs">
                                      {t("Auto-create")}
                                    </Badge>
                                  )}
                                </div>
                              ) : (
                                integration.channel_name && (
                                  <span className="text-sm text-gray-700">
                                    #{integration.channel_name}
                                  </span>
                                )
                              )}
                            </div>

                            {/* Right: Action Buttons */}
                            <div className="flex items-center space-x-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleToggleActive(integration)}
                                className="h-8 px-3 text-xs hover:bg-gray-200"
                                title={integration.is_active ? t("Pause integration") : t("Resume integration")}
                              >
                                {integration.is_active ? t("Pause") : t("Resume")}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenSetup(integrationType.type, integration)}
                                className="h-8 w-8 p-0 hover:bg-gray-200"
                                title={t("Configure")}
                              >
                                <Settings className="h-4 w-4 text-gray-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(integration.id, integration.integration_type)}
                                className="h-8 w-8 p-0 hover:bg-red-50"
                                title={t("Delete")}
                              >
                                <Trash2 className="h-4 w-4 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Separator Line (except for last item) */}
              {idx < integrationTypes.length - 1 && (
                <div className="border-b border-gray-300 my-4"></div>
              )}
            </div>
          );
        })}
      </div>

      {/* Setup Modal */}
      <Dialog open={showSetupModal} onOpenChange={setShowSetupModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedIntegration?.id ? t("Configure") : t("Connect")} {getIntegrationInfo(formData.integration_type)?.title}
            </DialogTitle>
            <DialogDescription>
              {t(getIntegrationInfo(formData.integration_type)?.description)}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {error && (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>{t(error)}</AlertDescription>
              </Alert>
            )}

            {success && (
              <Alert className="bg-green-50 border-green-200">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">{success}</AlertDescription>
              </Alert>
            )}

            {formData.integration_type === 'jira' ? (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t("Jira Site URL *")}
                  </label>
                  <Input
                    type="url"
                    placeholder="https://yourcompany.atlassian.net"
                    value={formData.jira_site_url}
                    onChange={(e) => setFormData({ ...formData, jira_site_url: e.target.value })}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {t("Your Jira Cloud instance URL")}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t("Email")} {selectedIntegration?.id ? '' : '*'}
                  </label>
                  <Input
                    type="email"
                    placeholder={selectedIntegration?.id ? t("Leave empty to keep current email") : "your-email@company.com"}
                    value={formData.jira_email}
                    onChange={(e) => setFormData({ ...formData, jira_email: e.target.value })}
                    autoComplete="off"
                    name="jira-email-integration"
                  />
                  {selectedIntegration?.jira_email && (
                    <p className="text-sm text-gray-500 mt-1">
                      {t("Current:")} {selectedIntegration.jira_email}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t("API Token")} {selectedIntegration?.id ? '' : '*'}
                  </label>
                  <Input
                    type="password"
                    placeholder={selectedIntegration?.id ? t("Leave empty to keep current token") : t("Your Jira API token")}
                    value={formData.jira_api_token}
                    onChange={(e) => setFormData({ ...formData, jira_api_token: e.target.value })}
                    autoComplete="new-password"
                    name="jira-api-token"
                  />
                  {selectedIntegration?.id ? (
                    <p className="text-sm text-gray-600 mt-1">
                      {t("✓ API Token is configured. Enter a new token only if you want to update it.")}
                    </p>
                  ) : (
                    <a
                      href={getIntegrationInfo('jira')?.setupUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline mt-1 inline-flex items-center"
                    >
                      {t("How to generate an API token")} <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {t("Project Key *")}
                    </label>
                    <Input
                      type="text"
                      placeholder="PROJ"
                      value={formData.jira_project_key}
                      onChange={(e) => setFormData({ ...formData, jira_project_key: e.target.value.toUpperCase() })}
                    />
                    <p className="text-sm text-gray-500 mt-1">
                      {t("e.g., PROJ, DEV")}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">
                      {t("Issue Type")}
                    </label>
                    <select
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={formData.jira_issue_type}
                      onChange={(e) => setFormData({ ...formData, jira_issue_type: e.target.value })}
                    >
                      <option value="Bug">{t("Bug")}</option>
                      <option value="Task">{t("Task")}</option>
                      <option value="Story">{t("Story")}</option>
                      <option value="Epic">{t("Epic")}</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="checkbox"
                    id="auto-create"
                    checked={formData.auto_create_on_failure}
                    onChange={(e) => setFormData({ ...formData, auto_create_on_failure: e.target.checked })}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="auto-create" className="text-sm">
                    {t("Automatically create Jira tickets on test failures")}
                  </label>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t(getIntegrationInfo(formData.integration_type)?.webhookLabel)}
                  </label>
                  <Input
                    type="url"
                    placeholder={
                      selectedIntegration?.id 
                        ? t("Leave empty to keep existing webhook URL")
                        : getIntegrationInfo(formData.integration_type)?.webhookPlaceholder
                    }
                    value={formData.webhook_url}
                    onChange={(e) => setFormData({ ...formData, webhook_url: e.target.value })}
                  />
                  {selectedIntegration?.id ? (
                    <p className="text-sm text-gray-600 mt-1">
                      {t("✓ Webhook URL is configured. Enter a new URL only if you want to update it.")}
                    </p>
                  ) : (
                    <a
                      href={getIntegrationInfo(formData.integration_type)?.setupUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:underline mt-1 inline-flex items-center"
                    >
                      {t("How to get a webhook URL")} <ExternalLink className="h-3 w-3 ml-1" />
                    </a>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    {t("Channel Name (Optional)")}
                  </label>
                  <Input
                    type="text"
                    placeholder={t("e.g., general, testing, alerts")}
                    value={formData.channel_name}
                    onChange={(e) => setFormData({ ...formData, channel_name: e.target.value })}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {t("A friendly name to help you identify this integration")}
                  </p>
                </div>
              </>
            )}

            <div className="flex space-x-3 pt-4">
              <Button
                onClick={handleSave}
                disabled={
                  (formData.integration_type === 'jira' 
                    ? !formData.jira_site_url || !formData.jira_email || !formData.jira_project_key || (!selectedIntegration?.id && !formData.jira_api_token)
                    : (!selectedIntegration?.id && !formData.webhook_url)
                  ) || saving
                }
                className="flex-1"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t("Saving...")}
                  </>
                ) : (
                  selectedIntegration?.id ? t("Update Integration") : t("Save Integration")
                )}
              </Button>
              
              {selectedIntegration?.id && (
                <Button
                  onClick={handleTestConnection}
                  disabled={testingConnection}
                  variant="outline"
                >
                  {testingConnection ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      {t("Testing...")}
                    </>
                  ) : (
                    formData.integration_type === 'jira' ? t("Create Test Issue") : t("Test Connection")
                  )}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmation.show} onOpenChange={(open) => !open && setDeleteConfirmation({ show: false, id: null, name: '' })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("Delete Integration")}</DialogTitle>
            <DialogDescription>
              {t("Are you sure you want to delete the {name} integration? This action cannot be undone.", { name: deleteConfirmation.name })}
            </DialogDescription>
          </DialogHeader>
          <div className="flex space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmation({ show: false, id: null, name: '' })}
              className="flex-1"
            >
              {t("Cancel")}
            </Button>
            <Button
              onClick={confirmDelete}
              className="flex-1 bg-red-600 hover:bg-red-700"
            >
              {t("Delete")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Integrations;
